import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import {
    Box,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api/client";
import "../../App.css";
import CustomPagination from "../../components/CustomPagination";
import { TableLoader } from "../../components/TableLoader";
import { TableNodata } from "../../components/TableNodata";
import useDebounce from "../../hooks/useDebounce";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { getLabelByKey, useChangeableItems } from "../../store/useDynamicLabel";
import { CalendarCheck2, Eye, Info, View } from "lucide-react";
import dayjs from "dayjs";

const InterviewFeedbackList = () => {

    const changeableItems = useChangeableItems();
    const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

    const tableref = useRef({});
    const navigate = useNavigate();
    const location = useLocation();
    const { userId, dashboard, instituteId, instituteName, TrainerName } =
        location.state || "";

    const [searchKey, setSearchKey] = useState(
        localStorage.getItem("searchvalue") || ""
    );
    const debouncedSearch = useDebounce(searchKey);
    const [loading, setLoading] = useState(false);
    const [count, setCount] = useState(0);
    const [listData, setListData] = useState([]);
    const [colleges, setColleges] = useState([]);
    const [trainersList, setTrainersList] = useState([]);
    const [batchOptions, setBatchOptions] = useState([]);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [appliedBatch, setAppliedBatch] = useState(null);
    const { showSnackbar } = useSnackbar();
    // Pagination state
    const [page, setPage] = useState(+localStorage.getItem("currentpage") || 0);
    const [rowsPerPage, setRowsPerPage] = useState(
        +localStorage.getItem("rowsPerPage") || 10
    );

    const handleSearch = (e) => {
        localStorage.setItem("currentPage", 0);
        setPage(0);
        localStorage.setItem("searchvalue", e.target.value);
        setSearchKey(e.target.value);
    };

    const studentList = async () => {
        setLoading(true);

        const cachekey = `page_${page}_size_${rowsPerPage}_searchkey_${debouncedSearch}`;

        if (tableref.current[cachekey]) {
            const cached = tableref.current[cachekey];
            setListData(cached?.data);
            setCount(cached?.count);
            setLoading(false);
            return;
        }

        try {
            const payload = {
                page: page,
                size: rowsPerPage,
                search_key: debouncedSearch,
            };
            const res = await api.post("ListMyInterviews", payload);
            if (res?.data?.status === "success") {
                const data = res?.data?.data;
                const count = res?.data?.count;

                setListData(res?.data?.data);
                setCount(res?.data?.count);
                setLoading(false);
            } else {
                setLoading(false);
            }
        } catch (error) {
            console.log("Error :", error);
            setLoading(false);
        }
    };

    useEffect(() => {
        studentList();
    }, [page, rowsPerPage, debouncedSearch]);

    useEffect(() => {
        localStorage.setItem("currentpage", page);
        localStorage.setItem("rowsPerPage", rowsPerPage);
    }, [page, rowsPerPage]);

    const getStatusStyle = (status) => {
        switch (status?.toLowerCase()) {
            case "scheduled":
                return {
                    // backgroundColor: "#FEF3C7",
                    // color: "#D97706",
                    backgroundColor: "#F3F4F6",
                    color: "#6B7280",
                };

            case "completed":
                return {
                    // backgroundColor: "#DCFCE7",
                    // color: "#16A34A",
                    backgroundColor: "#F3F4F6",
                    color: "#6B7280",
                };

            default:
                return {
                    backgroundColor: "#F3F4F6",
                    color: "#6B7280",
                };
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", p: 1 }}>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
            >
                <Grid
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 0.5,
                        alignItems: "center",
                    }}
                >
                    <CalendarCheck2 style={{ width: '32px', height: '32px', color: '#1e88e5' }} />
                    {/* Title */}
                    <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: "#0a0a0a", marginLeft: 1 }}>
                        Interview Schedules
                    </Typography>
                </Grid>
                {/* Search + Button */}
                <Box display="flex" gap={4} alignItems="center">
                    <TextField
                        value={searchKey}
                        onChange={(e) => handleSearch(e)}
                        size="small"
                        variant="outlined"
                        placeholder={`Search candidate`}
                        className="custom-search-field"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            </Box>

            <TableContainer
                component={Paper}
                sx={{
                    mt: 3,
                    transition: "opacity 0.5s ease, transform 0.5s ease",
                }}
            >
                <Table>
                    <TableHead>
                        <TableRow className="tableHeader">
                            <TableCell className="tableHeader">
                                <b>S.No</b>
                            </TableCell>
                            <TableCell className="tableHeader">
                                <b>{`${getLabel('STUDENT')}`}</b>
                            </TableCell>
                            <TableCell className="tableHeader">
                                <b>{`${getLabel('PROGRAMS')}`}</b>
                            </TableCell>
                            <TableCell className="tableHeader">
                                <b>Status</b>
                            </TableCell>
                            <TableCell className="tableHeader">
                                <b>Reviewd By</b>
                            </TableCell>
                            <TableCell className="tableHeader">
                                <b>Scheduled At</b>
                            </TableCell>
                            <TableCell className="tableHeader">
                                <b>Action</b>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    {loading ? (
                        <TableCell colSpan={6} align="center">
                            <TableLoader message="Fetching datas..." />
                        </TableCell>
                    ) : (
                        <TableBody className="tablebody">
                            {listData?.map((item, index) => (
                                <TableRow
                                    key={item.interview_id}
                                    hover
                                    sx={{
                                        backgroundColor: index % 2 === 1 ? "#FAFAFA" : "inherit",
                                        "&:hover": { backgroundColor: "#f1f1f1" },
                                    }}
                                >
                                    <TableCell align="center">
                                        {page * rowsPerPage + index + 1}
                                    </TableCell>
                                    <TableCell>
                                        <span style={{ fontWeight: "bold" }}>
                                            {item?.candidate?.first_name + " " + item?.candidate?.last_name || "-"}
                                        </span>
                                        <br />
                                        {item?.candidate?.email}
                                    </TableCell>
                                    <TableCell>
                                        <span style={{ fontWeight: "bold" }}>
                                            {item?.program_name || "-"}
                                        </span>
                                    </TableCell>
                                    <TableCell sx={{ alignItems: 'center', display: 'flex' }}>
                                        <span
                                            style={{
                                                display: "inline-block",
                                                padding: "4px 10px",
                                                borderRadius: "6px",
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                textTransform: "capitalize",
                                                ...getStatusStyle(item?.status),
                                            }}
                                        >
                                            {
                                                item?.interviewer_response_status?.toLowerCase() === 'rejected' ?
                                                    item?.interviewer_response_status :
                                                    item.status
                                                    || "-"
                                            }
                                        </span>

                                        {(item?.interviewer_response_status?.toLowerCase() === 'rejected' || item?.status?.toLowerCase() === 'cancelled') && (
                                            <Tooltip
                                                title={
                                                    item?.interviewer_response_status?.toLowerCase() === 'rejected'
                                                        ? `INTERVIEWER: ${item?.interviewer_rejection_reason}`
                                                        : `HR: ${item?.deletion_reason}`
                                                }
                                                style={{
                                                    marginLeft: '3px',
                                                    display: 'inline-flex'
                                                }}
                                            >
                                                <Info
                                                    size={20}
                                                    strokeWidth={2}
                                                    color="#1c97e3"
                                                />
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: '250px' }}>
                                        <span style={{ fontWeight: "bold" }}>
                                            {item?.reviewed_by || "-"}
                                        </span>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: '250px' }} title={item?.ai_feedback}>
                                        <span style={{ fontWeight: "bold" }}>
                                            {item?.scheduled_at ? dayjs(item?.scheduled_at)?.format("DD-MM-YYYY, hh:mm A") : null}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                            }}
                                            onClick={() => navigate("/interview-feedback", {
                                                state:
                                                {
                                                    interviewId: item?.interview_id,
                                                    interviewStatus: item?.interviewer_response_status?.toLowerCase() === 'rejected' ?
                                                        item?.interviewer_response_status :
                                                        item.status

                                                }
                                            })}
                                        >
                                            <Eye size={16} color="#1C97E3" />
                                            <Typography color="primary" sx={{ fontSize: "12px" }}>
                                                View
                                            </Typography>
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}

                            {((page === 0 && listData?.length > rowsPerPage - 1) ||
                                page !== 0) && (
                                    <CustomPagination
                                        page={page}
                                        rowsPerPage={rowsPerPage}
                                        count={count}
                                        setPage={setPage}
                                        colSpan={6}
                                    />
                                )}

                            {listData?.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <TableNodata />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    )}
                </Table>
            </TableContainer>
        </Box>
    );
};

export default InterviewFeedbackList;
